import { PersonaCard } from "@/components/blocks/persona-card";
import { QAItem } from "@/components/blocks/qa-item";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getPersonasFromQAs,
  getQAsByPersonaId,
  image_qas,
  Persona,
  QA,
} from "@/lib/constants";
import { useEffect, useState } from "react";

export function ImageEvalDemo() {
  // Analysis data for image evaluation
  const positiveAspects = [
    {
      category: "Visual Impact",
      description:
        "Vibrant pastel color palette creates an eye-catching display that immediately conveys a spring/summer seasonal theme",
    },
    {
      category: "Product Showcase",
      description:
        "Excellent product presentation showing multiple container types with consistent design language across different formats",
    },
    {
      category: "Background Design",
      description:
        "Abstract geometric background complements the products without competing for attention, enhancing the modern aesthetic",
    },
  ];

  const improvementAreas = [
    {
      category: "Product Visibility",
      description:
        "The light mint tumbler somewhat blends into similar background colors, slightly reducing its visual impact",
    },
    {
      category: "Brand Presence",
      description:
        "Starbucks logo is visible but relatively subtle on some items, potentially reducing brand recognition at smaller scales",
    },
  ];

  const keyPoints = [
    {
      label: "Color Palette",
      description:
        "Trendy pastel color scheme creates a cohesive collection that appeals to target demographic",
      type: "primary",
    },
    {
      label: "Textured Design",
      description:
        "Embossed arc pattern adds visual interest and tactile appeal across the collection",
      type: "primary",
    },
    {
      label: "Product Variety",
      description:
        "Diverse container types showcase the breadth of the collection while maintaining design unity",
      type: "primary",
    },
    {
      label: "Background Contrast",
      description:
        "Some products could benefit from stronger contrast against the background",
      type: "secondary",
    },
    {
      label: "Arrangement",
      description:
        "Linear arrangement is clean but somewhat conventional, missing opportunity for creative styling",
      type: "secondary",
    },
  ];

  const visualAnalysis = [
    {
      category: "Design Cohesion",
      description:
        "The embossed arc pattern creates a strong design signature that unifies the collection while each piece maintains its unique identity through different colors and formats.",
    },
    {
      category: "Color Psychology",
      description:
        "The pastel color palette conveys freshness, playfulness and optimism, perfect for spring/summer merchandise and appeals strongly to younger demographics and trend-conscious consumers.",
    },
    {
      category: "Aesthetic Trend Alignment",
      description:
        "The collection successfully incorporates current design trends in color and texture while maintaining the recognizable Starbucks premium feel, positioning the products as both fashionable and collectible.",
    },
  ];

  const [qaData, setQaData] = useState<QA[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | null>(
    null
  );
  const [filteredQAs, setFilteredQAs] = useState<QA[]>([]);

  const handlePersonaClick = (personaId: number) => {
    setSelectedPersonaId(personaId);
    setFilteredQAs(getQAsByPersonaId(qaData, personaId));
  };

  useEffect(() => {
    // In a real app, you would fetch this data from an API
    // For this example, we'll use the data from the snippet
    const fetchData = async () => {
      try {
        // This would be a fetch call in a real app

        setQaData(image_qas);
        const extractedPersonas = getPersonasFromQAs(image_qas);
        setPersonas(extractedPersonas);

        if (extractedPersonas.length > 0) {
          setSelectedPersonaId(extractedPersonas[0].id);
          setFilteredQAs(getQAsByPersonaId(image_qas, extractedPersonas[0].id));
        }

        // setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        // setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        {/* <header className="flex justify-between items-center py-4 mb-6 border-b border-slate-200">
          <div className="text-2xl font-bold text-purple-600">consumr.ai</div>
          <div className="flex gap-4">
            <button className="bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-800 hover:bg-slate-100 w-10 h-10 rounded-full flex items-center justify-center transition-all">
              <Bell size={18} />
            </button>
            <button className="bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-800 hover:bg-slate-100 w-10 h-10 rounded-full flex items-center justify-center transition-all">
              <User size={18} />
            </button>
          </div>
        </header> */}

        {/* Page Title */}
        <h1 className="text-2xl font-semibold mb-6">
          Creative Image Evaluation Report
        </h1>
        <div className="inline-block bg-slate-100 rounded-full px-4 py-2 text-sm mb-4">
          StarbucksSpring2025Collection.jpg
        </div>

        {/* Image Preview Container */}
        <div className="relative bg-white rounded-lg overflow-hidden mb-6 h-96 flex items-center justify-center">
          <img
            className="object-contain w-full h-full"
            src="https://imagedelivery.net/eqKNURtNCms0uspDZDkHSQ/198a48b4-05d7-41d8-d5ef-ac2c7ec35e00/public"
            alt=""
          />
        </div>

        {/* Score Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Visual Appeal</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                8<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Brand Consistency</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                9<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Audience Resonance</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                7<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Overall Score</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                8<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Technical Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Technical Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Image Type</p>
                <p className="font-medium">Product Collection</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Products Shown</p>
                <p className="font-medium">4 Items</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Primary Colors</p>
                <p className="font-medium">Pastels</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Background Style</p>
                <p className="font-medium">Abstract Geometric</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conclusion Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Conclusion</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              This product lineup image effectively showcases Starbucks' new
              drinkware collection against a playful pastel background. The
              variety of container styles (can, tumbler, cold cup, and mug)
              demonstrates the range while maintaining a cohesive aesthetic
              through the embossed pattern design. The bright, cheerful color
              palette perfectly captures a spring/summer seasonal vibe that will
              likely resonate strongly with the target audience.
            </p>

            <Accordion type="single" collapsible>
              <AccordionItem value="rationale">
                <AccordionTrigger>See Detailed Analysis</AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h3 className="font-semibold mb-3">Positive Aspects</h3>
                      {positiveAspects.map((aspect, index) => (
                        <div
                          key={index}
                          className="bg-slate-100 rounded-lg p-3 mb-3"
                        >
                          <div className="inline-block bg-purple-400 text-white px-2 py-1 rounded text-xs mb-2">
                            {aspect.category}
                          </div>
                          <p>{aspect.description}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">
                        Areas for Improvement
                      </h3>
                      {improvementAreas.map((area, index) => (
                        <div
                          key={index}
                          className="bg-slate-100 rounded-lg p-3 mb-3"
                        >
                          <div className="inline-block bg-purple-400 text-white px-2 py-1 rounded text-xs mb-2">
                            {area.category}
                          </div>
                          <p>{area.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/*  */}
        <div className="container mx-auto py-8 px-4">
          <h3 className="text-xl font-bold mb-8 text-center">
            AI Twin Responses
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {personas.map((persona) => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                isSelected={selectedPersonaId === persona.id}
                onClick={() => handlePersonaClick(persona.id)}
              />
            ))}
          </div>

          {selectedPersonaId && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">
                Responses History -{""}{" "}
                {personas.find((p) => p.id === selectedPersonaId)?.name}
              </h2>

              {filteredQAs.length > 0 ? (
                <div>
                  {filteredQAs.map((qa, index) => (
                    <QAItem key={index} qa={qa} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">
                  No Q&A history available for this persona.
                </p>
              )}
            </div>
          )}
        </div>
        {/*  */}

        {/* Visual Elements Analysis Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Visual Elements Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-3">Composition</h3>
                <div className="h-48 bg-slate-200 rounded-lg mb-3 flex items-center justify-center text-slate-500">
                  <img
                    src="https://imagedelivery.net/eqKNURtNCms0uspDZDkHSQ/39436b31-e319-4506-1a58-656b46b62c00/public"
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-left text-sm">
                  The products are arranged in a clean linear format with even
                  spacing, creating a balanced catalog-style presentation. The
                  arrangement progresses from tallest to shortest, creating a
                  natural visual rhythm from left to right.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Color Analysis</h3>
                <div className="flex gap-2 mb-3 ">
                  <div
                    className="w-12 h-12 rounded-md"
                    style={{ backgroundColor: "#a4caba" }}
                  ></div>
                  <div
                    className="w-12 h-12 rounded-md"
                    style={{ backgroundColor: "#a8e6d5" }}
                  ></div>
                  <div
                    className="w-12 h-12 rounded-md"
                    style={{ backgroundColor: "#9b75a4" }}
                  ></div>
                  <div
                    className="w-12 h-12 rounded-md"
                    style={{ backgroundColor: "#e4b6a5" }}
                  ></div>
                  <div
                    className="w-12 h-12 rounded-md"
                    style={{ backgroundColor: "#c9e3a1" }}
                  ></div>
                </div>
                <p className="text-left text-sm">
                  The collection features a split-complementary color scheme
                  with metallic mint, gradient teal, purple, and dusty rose. The
                  pastel intensity creates a cohesive palette despite the varied
                  hues, perfectly aligning with 2025 color trends in home goods
                  and fashion.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Audience Feedback Card */}
        <Card className="mb-6">
          <Accordion type="single" collapsible>
            <AccordionItem value="audience-feedback">
              <CardHeader className="px-6 py-4 pb-0">
                <AccordionTrigger className="py-2">
                  <CardTitle className="text-lg font-semibold">
                    Audience Feedback
                  </CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  {/* Feedback 1 */}
                  <div className="bg-slate-100 rounded-lg p-4 mb-4">
                    <div className="font-semibold mb-2">
                      Visual Appeal Assessment:
                    </div>
                    <div className="text-purple-600 mb-2">
                      How appealing is this drinkware collection? Would it catch
                      your eye while scrolling?
                    </div>
                    <p className="mb-4 text-left">
                      The Starbucks drinkware collection has excellent visual
                      appeal with its trendy pastel colors and distinctive
                      embossed pattern. The combination of different container
                      formats with a consistent design language creates an
                      immediately recognizable collection that would stand out
                      in social feeds.
                    </p>

                    <h3 className="font-semibold mb-3">Key Points</h3>
                    <table className="w-full border-collapse">
                      <tbody>
                        {keyPoints.map((point, index) => (
                          <tr key={index} className="border-b border-slate-200">
                            <td className="py-2 pr-4 text-left">
                              <Badge
                                variant={
                                  point.type === "primary"
                                    ? "default"
                                    : "secondary"
                                }
                                className={
                                  point.type === "primary"
                                    ? "bg-purple-500"
                                    : ""
                                }
                              >
                                {point.label}
                              </Badge>
                            </td>
                            <td className="py-2 text-left">
                              {point.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Feedback 2 */}
                  <div className="bg-slate-100 rounded-lg p-4 mb-4">
                    <div className="font-semibold mb-2">
                      Visual Storytelling:
                    </div>
                    <div className="text-purple-600 mb-2">
                      Does this collection effectively convey a cohesive brand
                      story while offering variety?
                    </div>
                    <p className="mb-4 text-left">
                      The collection successfully balances unity and variety -
                      each item shares the embossed arc pattern but offers
                      consumers different format choices and colorways. The
                      image effectively communicates that this is a cohesive
                      seasonal collection while showcasing the range of options
                      available to different consumer preferences.
                    </p>

                    <h3 className="font-semibold mb-3">Visual Analysis</h3>
                    {visualAnalysis.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg p-3 mb-3 shadow-sm"
                      >
                        <div className="inline-block bg-purple-400 text-white px-2 py-1 rounded text-xs mb-2">
                          {item.category}
                        </div>
                        <p>{item.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Consumer Personas Card */}
        {/* <Card className="mb-6">
          <Accordion type="single" collapsible>
            <AccordionItem value="consumer-personas">
              <CardHeader className="px-6 py-4 pb-0">
                <AccordionTrigger className="py-2">
                  <CardTitle className="text-lg font-semibold">
                    Consumer Personas Response
                  </CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {twins.map((twin) => (
                      <div
                        key={twin.id}
                        className="flex flex-col items-center bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all hover:translate-y-[-5px] hover:shadow-md"
                      >
                        <div className="w-16 h-16 rounded-full bg-slate-200 mb-3 overflow-hidden">
                          <img
                            src={twin.avatar}
                            alt={`${twin.name} avatar`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="font-semibold text-center mb-1">
                          {twin.name}
                        </div>
                        <div className="text-xs text-slate-500 text-center">
                          Stage: {twin.stage}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card> */}

        {/* Platform Optimization */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Platform Optimization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold">Instagram</h3>
                </div>
                <div className="text-sm text-slate-500">Optimization Score</div>
                <div className="text-xl font-bold mb-2">95%</div>
                <p className="text-sm">
                  Perfect for Instagram's visual-first approach. Image
                  dimensions are optimized for feed posts.
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold">X/Twitter</h3>
                </div>
                <div className="text-sm text-slate-500">Optimization Score</div>
                <div className="text-xl font-bold mb-2">85%</div>
                <p className="text-sm">
                  Good fit but consider adding text elements for better
                  engagement on this platform.
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold">YouTube</h3>
                </div>
                <div className="text-sm text-slate-500">Optimization Score</div>
                <div className="text-xl font-bold mb-2">75%</div>
                <p className="text-sm">
                  Would need to be reformatted for thumbnail use. Consider
                  horizontal orientation for this platform.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {/* <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" className="flex items-center gap-2">
            <Download size={16} />
            Download Report
          </Button>
          <Button className="flex items-center gap-2">
            <Share size={16} />
            Share Report
          </Button>
        </div> */}
      </div>
    </div>
  );
}
